import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import _ from "lodash";

import {
  editCreative,
  clearSelectedCreatives,
  selectCreative
} from "../../actions/app/creatives";
import { createName } from "../../utils/creative";
import { insertCreatives } from "../../services/dcm/creatives";
import CreativeNameGenerator from "./CreativeNameGenerator";

// default state objects

// these objects are provided to prevent 'undefined'
// errors when reading the properties of the objects

const defaultErrorObject = {
  isError: false,
  urlId: { isError: false, message: "" },
  make: { isError: false, message: "" },
  modelYear: { isError: false, message: "" },
  model: { isError: false, message: "" },
  trim: { isError: false, message: "" },
  creativeAgency: { isError: false, message: "" },
  strategy: { isError: false, message: "" },
  retailOfferType: { isError: false, message: "" },
  creativeUnit: { isError: false, message: "" },
  extraInfo: { isError: false, message: "" },
  format: { isError: false, message: "" },
  country: { isError: false, message: "" },
  language: { isError: false, message: "" },
  creativeVersion: { isError: false, message: "" },
  dimensions: { isError: false, message: "" },
  creativeDisplay: { isError: false, message: "" }
};

const defaultTaxonomy = { name: "" };

const regExp = /"|»|_|\+|#|&|\.|\//g;

// Map a freshly-uploaded file's MIME type to its taxonomy format slot.
// Used during bulk save so a single Format selection applied across mixed
// uploads is overridden per-creative by what the file actually is.
const getFormatFromFileType = (fileType) => {
  if (!fileType) return null;
  if (fileType.includes("zip") || fileType.includes("application")) return "H5S";
  if (fileType.startsWith("audio/")) return "Audio";
  if (fileType.startsWith("video/")) return "Video";
  if (fileType.startsWith("image/")) return "Static";
  return null;
};

// Formats whose mismatch has a hard consequence in Campaign Manager.
const MEDIA_FORMATS = new Set(["Audio", "Video"]);

// Detects a Format selection that contradicts the uploaded file where it
// actually matters. CM360 derives the creative type from the asset, so an MP4
// saved as "Audio" is inserted as INSTREAM_VIDEO and can never be trafficked
// against an audio placement (and the reverse for an MP3 saved as "Video").
// Deliberately limited to cases where Audio or Video is on one side: a .zip
// can legitimately be labelled more than one way, so those keep falling
// through to the silent per-file derivation in handleSave.
const getMediaFormatMismatch = (creative, chosenFormat) => {
  if (!creative || creative.dcmCreative) return null;
  const derived = getFormatFromFileType(creative.file && creative.file.type);
  if (!derived || !chosenFormat) return null;
  if (derived === chosenFormat) return null;
  if (!MEDIA_FORMATS.has(derived) && !MEDIA_FORMATS.has(chosenFormat)) return null;
  return {
    derived,
    chosen: chosenFormat,
    fileName: (creative.file && creative.file.name) || "The uploaded file"
  };
};

const validateTaxonomy = (
  taxonomy,
  standalone = false,
  isDcmCreative = false,
  pathname = "",
  fileType = ""
) => {
  let validTaxonomy = [];
  if (standalone) {
    validTaxonomy = [
      "make",
      "modelYear",
      "model",
      "trim",
      "creativeAgency",
      "strategy",
      "retailOfferType",
      "creativeUnit",
      "extraInfo",
      "format",
      "country",
      "language",
      "creativeVersion",
      "dimensions",
      "creativeDisplay"
    ];
  } else {
    validTaxonomy = [
      "urlId",
      "make",
      "modelYear",
      "model",
      "trim",
      "creativeAgency",
      "strategy",
      "retailOfferType",
      "creativeUnit",
      "extraInfo",
      "format",
      "country",
      "language",
      "creativeVersion",
      "dimensions",
      "creativeDisplay"
    ];
  }

  let errors = _.defaultsDeep({}, defaultErrorObject); //set errors to default object

  if (isDcmCreative) return errors;

  for (let property of validTaxonomy) {
    //if taxonomy object doesn't include property
    if (
      property !== "trim" &&
      property !== "extraInfo" &&
      property !== "urlId"
    ) {
      if (!taxonomy[property]) {
        if (!errors.isError) errors.isError = true; // set error flag for error object
        errors[property] = { isError: true, message: "Field missing" }; // set error for taxonomy missing
      }
    }
    if (property !== "urlId" && regExp.test(taxonomy[property])) {
      if (!errors.isError) errors.isError = true; // set error flag for error object
      errors[property] = {
        isError: true,
        message: '" _ + # & . / and » characters are not permitted'
      }; // set error for prohibited characters
    }

    if (property === "urlId") {
      if (pathname.includes("preloader")) {
        if (fileType.includes("zip")) {
          if (!taxonomy[property]) {
            if (!errors.isError) errors.isError = true; // set error flag for error object
            errors[property] = { isError: true, message: "Field missing" }; // set error for taxonomy missing
          }
        }
      }

      if (pathname.includes("display")) {
        if (!taxonomy[property]) {
          if (!errors.isError) errors.isError = true; // set error flag for error object
          errors[property] = { isError: true, message: "Field missing" }; // set error for taxonomy missing
        }
      }
    }
  }

  return errors;
};

const CreativeNameManager = ({ standalone, advertiserId }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  let [errors, setErrors] = useState(defaultErrorObject);
  let [taxonomy, setTaxonomy] = useState(defaultTaxonomy); //default state
  let [weight, setWeight] = useState(0);

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  let creativesState = useSelector((state) => state.creatives);
  let selectedIds = creativesState.selectedIds;
  let selectedCreative = creativesState.byId[creativesState.selectedId];

  // Every field below is gated on selectedCreative, but the upload step has no
  // selectable cards — so a flow that runs straight from upload to naming
  // (audio/video with no companion, Display with no backup) arrives with
  // nothing selected and the whole form reads as broken. Select the first
  // primary on arrival. Guarded by a ref so it happens once per visit: a user
  // who deliberately deselects a card should not have it snap back.
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (standalone || didAutoSelectRef.current) return;
    if (creativesState.byId[creativesState.selectedId]) {
      didAutoSelectRef.current = true;
      return;
    }
    let firstPrimary = creativesState.allIds
      .map((id) => creativesState.byId[id])
      .find((c) => c && (c.creativeRole || "PRIMARY") === "PRIMARY");
    // No creatives yet — leave the ref alone so this runs again once they land.
    if (!firstPrimary) return;
    didAutoSelectRef.current = true;
    dispatch(selectCreative(firstPrimary.id));
  }, [standalone, creativesState, dispatch]);

  useEffect(() => {
    if (creativesState.allIds.length > 0) {
      for (let creative of Object.values(creativesState.byId)) {
        if (creative.dimensions !== "N/A" && !creative.taxonomy.dimensions) {
          let updatedCreative = _.defaultsDeep({}, creative);
          updatedCreative.taxonomy.dimensions = updatedCreative.dimensions
            .split(" ")
            .join("");
           dispatch(editCreative(updatedCreative)); 
        }   
      }
    }
  }, [creativesState, dispatch]);

  const getCreateNameOptions = (creative) => {
    if (!creative || !creative.file || !creative.file.type) return {};
    let isMedia = creative.file.type.startsWith("audio/") || creative.file.type.startsWith("video/");
    if (!isMedia || creative.creativeRole !== "PRIMARY") return {};
    let companion = creative.companionId ? creativesState.byId[creative.companionId] : null;
    let companionDimensions = companion && companion.dimensions && companion.dimensions !== "N/A"
      ? companion.dimensions
      : null;
    return {
      isMediaCreative: true,
      hasCompanion: !!creative.companionId,
      companionDimensions
    };
  };

  useEffect(() => {
    if (selectedCreative) {
      let loadedTaxonomy = { ...selectedCreative.taxonomy };

      // For companions, inherit fields from the parent primary if not yet set
      if (selectedCreative.creativeRole === "COMPANION") {
        // Find the primary that owns this companion
        let parentPrimary = Object.values(creativesState.byId).find(
          (c) => c.companionId === selectedCreative.id
        );

        if (parentPrimary && parentPrimary.taxonomy) {
          // Inherit all fields from primary except format, dimensions, creativeDisplay, and name
          for (let key of Object.keys(parentPrimary.taxonomy)) {
            if (key === "name" || key === "format" || key === "dimensions" || key === "creativeDisplay") continue;
            if (!loadedTaxonomy[key]) {
              loadedTaxonomy[key] = parentPrimary.taxonomy[key];
            }
          }
        }

        // Auto-populate format based on companion file type
        if (selectedCreative.file && !loadedTaxonomy.format) {
          if (selectedCreative.file.type && selectedCreative.file.type.startsWith("image/")) {
            loadedTaxonomy.format = "Static";
          } else if (selectedCreative.file.type && selectedCreative.file.type.includes("zip")) {
            loadedTaxonomy.format = "H5S";
          }
        }
      }

      // Force Creative Display to "N/A" for all non-video creatives
      if (selectedCreative.file && selectedCreative.file.type &&
          !selectedCreative.file.type.startsWith("video/")) {
        loadedTaxonomy.creativeDisplay = "NA";
      }

      setTaxonomy(loadedTaxonomy);
      setWeight(selectedCreative.weight);
    } else {
      setTaxonomy(defaultTaxonomy);
      setWeight(0);
    }
    setErrors(defaultErrorObject);
  }, [selectedCreative, creativesState]);

  const handleSelect = (option, action) => {
    let taxonomyValues = { ...taxonomy }; //copy state

    if (option.kind === "newDcmLandingPage") {
      taxonomyValues[action.name] = option.url;
    } else if (option.kind) {
      taxonomyValues[action.name] = option.id; //if it's from DCM, use the DCM ID as the value
    } else {
      taxonomyValues[action.name] = option.value; //set object property based on <Select /> name
    }

    if (selectedCreative.dcmCreative) {
      setTaxonomy(taxonomyValues); // Existing creatives already have taxonomy name, we do not update it
    } else {
      setTaxonomy(createName(taxonomyValues, getCreateNameOptions(selectedCreative))); //create name and include in taxonomy object
    }
  };

  const handleTextInput = (event) => {
    let taxonomyValues = { ...taxonomy };
    taxonomyValues[event.target.name] = event.target.value;
    if (selectedCreative.dcmCreative) {
      // Existing GCM creatives already have a name in DCM; don't regenerate it from the form
      setTaxonomy(taxonomyValues);
    } else {
      setTaxonomy(createName(taxonomyValues, getCreateNameOptions(selectedCreative)));
    }
  };

  // severity "error" renders red and stays up until the user dismisses it —
  // the format-mismatch text is long enough that a 3s auto-hide cut it off.
  const setAlert = (message, severity = "success") => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleClearValues = () => {
    setTaxonomy(defaultTaxonomy); //set select values back to default state
  };

  const handleRetinaToggle = (checked) => {
    if (!selectedCreative || !selectedCreative.dimensions || selectedCreative.dimensions === "N/A") return;

    let updatedCreative = _.defaultsDeep({}, selectedCreative);
    updatedCreative.isRetina = checked;

    let taxonomyValues = { ...taxonomy };

    if (checked) {
      // Halve dimensions for retina
      let dims = selectedCreative.dimensions.split(" x ");
      if (dims.length === 2) {
        taxonomyValues.dimensions = `${Math.round(parseInt(dims[0]) / 2)}x${Math.round(parseInt(dims[1]) / 2)}`;
      }
    } else {
      // Restore original dimensions
      taxonomyValues.dimensions = selectedCreative.dimensions.split(" ").join("");
    }

    updatedCreative.taxonomy = { ...updatedCreative.taxonomy, ...taxonomyValues };
    let nameOpts = getCreateNameOptions(selectedCreative);
    updatedCreative.taxonomy = createName(updatedCreative.taxonomy, nameOpts);
    dispatch(editCreative(updatedCreative));
    setTaxonomy(createName(taxonomyValues, nameOpts));
  };

  const handleSave = () => {
    // Only save creatives matching the currently selected creative's role
    // This prevents applying PRIMARY taxonomy to COMPANIONs when both are in selectedIds
    let activeRole = selectedCreative ? selectedCreative.creativeRole : "PRIMARY";

    // Block a single save whose Format contradicts the file (QA request: an
    // MP4 named as Audio must not slip through). Only enforced for a
    // one-creative save — a bulk save across a mixed batch can't have one
    // correct Format, so those intentionally fall through to the
    // per-creative derivation below.
    let savingIds = selectedIds.filter((id) => {
      let c = creativesState.byId[id];
      return c && c.creativeRole === activeRole;
    });

    if (savingIds.length === 1) {
      let mismatch = getMediaFormatMismatch(
        creativesState.byId[savingIds[0]],
        taxonomy.format
      );

      if (mismatch) {
        setErrors({ ...defaultErrorObject, format: { isError: true, message: "" } });
        setAlert(
          `"${mismatch.fileName}" is a ${mismatch.derived} file but Format is set to ` +
            `${mismatch.chosen}. Campaign Manager reads the creative type from the ` +
            `file, so this would traffic as ${mismatch.derived} and cannot run on a ` +
            `${mismatch.chosen} placement. Set Format to ${mismatch.derived}, or ` +
            `upload a ${mismatch.chosen} file.`,
          "error"
        );
        return;
      }
    }

    for (let id of selectedIds) {
      let currentCreative = _.defaultsDeep({}, creativesState.byId[id]);
      if (currentCreative.creativeRole !== activeRole) continue;

      let updatedCreative = { taxonomy: {} };

      setErrors(defaultErrorObject); //reset errors before checking

      if (currentCreative.creativeRole === "PRIMARY") {
        let isDcmCreative = currentCreative && currentCreative.dcmCreative;
        let dimensionsMatch =
          currentCreative.taxonomy.dimensions === taxonomy.dimensions;
        let fileType = "";

        if (!isDcmCreative) {
          fileType = currentCreative.file.type;
        }

        let errors = validateTaxonomy(
          taxonomy,
          standalone,
          isDcmCreative,
          location.pathname,
          fileType
        ); // check taxonomy for errors

        if (!errors.isError) {
          let taxonomyCopy = _.defaultsDeep({}, taxonomy);

          taxonomyCopy.externalId = ""

          if (isDcmCreative) {
            //ignore name and only update URL when the creative is from DCM
            updatedCreative = {
              ...currentCreative,
              taxonomy: {
                ...currentCreative.taxonomy,
                urlId: taxonomyCopy.urlId
              }
            };
          } else {
            updatedCreative = {
              ...currentCreative,
              taxonomy: taxonomyCopy
            };

            if (currentCreative.dimensions !== "N/A" && !dimensionsMatch) {
              //revert dimensions
              updatedCreative.taxonomy.dimensions =
                currentCreative.taxonomy.dimensions;
            } else {
              // Even when there's no auto-derived dimension to revert to,
              // reject a bulk-applied dimension whose format doesn't match
              // the file type (e.g., a "300x250" picked from the dropdown
              // shouldn't land on an audio creative, and ":30" shouldn't
              // land on an image).
              const fileType =
                currentCreative.file && currentCreative.file.type;
              const isMediaCreative =
                fileType &&
                (fileType.startsWith("audio/") || fileType.startsWith("video/"));
              const isImageCreative = fileType && fileType.startsWith("image/");
              const dropdownVal = taxonomy && taxonomy.dimensions;
              const isDurationFormat =
                typeof dropdownVal === "string" && dropdownVal.startsWith(":");
              const isWidthHeightFormat =
                typeof dropdownVal === "string" && /^\d+x\d+$/.test(dropdownVal);

              if (
                (isMediaCreative && isWidthHeightFormat) ||
                (isImageCreative && isDurationFormat)
              ) {
                updatedCreative.taxonomy.dimensions =
                  currentCreative.taxonomy.dimensions || "";
              }
            }

            // Override format with the file-type-derived value so a bulk
            // Format selection doesn't apply (e.g.) "Static" to an audio file.
            const derivedFormat =
              currentCreative.file && getFormatFromFileType(currentCreative.file.type);
            if (derivedFormat) {
              updatedCreative.taxonomy.format = derivedFormat;
            }

            if (currentCreative.externalId && currentCreative.externalId.length > 0) {
              updatedCreative.taxonomy.externalId = currentCreative.externalId;
            }

            updatedCreative.taxonomy = createName(updatedCreative.taxonomy, getCreateNameOptions(currentCreative));
          }

          dispatch(editCreative(updatedCreative)); //update object in redux store
          if (updatedCreative.backupId) {
            // update backup with same taxonomy name — unless backup is from DCM (already named)
            let updatedBackupCreative =
              creativesState.byId[updatedCreative.backupId];
            if (!updatedBackupCreative.dcmCreative) {
              updatedBackupCreative.taxonomy = updatedCreative.taxonomy;
              dispatch(editCreative(updatedBackupCreative));
            }
          }
          // Companions have independent taxonomy — do not auto-sync
          setErrors(defaultErrorObject); //reset form errors
          setAlert("Taxonomy saved successfully!");
          dispatch(clearSelectedCreatives());
        } else {
          setErrors(errors);
          setAlert(
            "There was a problem with the taxonomy. Please update and try again.",
            "error"
          );
        }
      } else if (currentCreative.creativeRole === "COMPANION") {
        let dimensionsMatch =
          currentCreative.taxonomy.dimensions === taxonomy.dimensions;

        let errors = validateTaxonomy(
          taxonomy,
          standalone,
          false,
          location.pathname,
          currentCreative.file ? currentCreative.file.type : ""
        );

        if (!errors.isError) {
          let taxonomyCopy = _.defaultsDeep({}, taxonomy);
          taxonomyCopy.externalId = "";

          updatedCreative = {
            ...currentCreative,
            taxonomy: taxonomyCopy
          };

          // Lock companion dimensions to its own image size
          if (currentCreative.dimensions !== "N/A" && !dimensionsMatch) {
            updatedCreative.taxonomy.dimensions =
              currentCreative.taxonomy.dimensions;
          }

          // Same per-file format derivation as the PRIMARY branch.
          const derivedCompanionFormat =
            currentCreative.file && getFormatFromFileType(currentCreative.file.type);
          if (derivedCompanionFormat) {
            updatedCreative.taxonomy.format = derivedCompanionFormat;
          }

          updatedCreative.taxonomy = createName(updatedCreative.taxonomy);
          dispatch(editCreative(updatedCreative));
          setErrors(defaultErrorObject);
          setAlert("Companion taxonomy saved successfully!");
          dispatch(clearSelectedCreatives());
        } else {
          setErrors(errors);
          setAlert(
            "There was a problem with the taxonomy. Please update and try again.",
            "error"
          );
        }
      }
    }
  };

  const handleSubmit = () => {
    insertCreatives(
      Object.values(creativesState.byId),
      creativesState.assignedPlacements,
      "DISPLAY"
    );
  };

  const handleFileDimensions = () => {
    if (selectedCreative && selectedCreative.dimensions !== "N/A") {
      let trimmedDimensions = selectedCreative.dimensions.split(" ").join("");
      return trimmedDimensions;
    }

    return undefined;
  };

  return (
    <div style={{ width: "100%" }}>
      <CreativeNameGenerator
        taxonomy={taxonomy}
        handleSelect={handleSelect}
        handleClearValues={handleClearValues}
        handleSave={handleSave}
        handleSubmit={handleSubmit}
        handleTextInput={handleTextInput}
        isDisabled={!selectedCreative}
        errors={errors}
        isError={errors.isError}
        standalone={standalone}
        advertiserId={advertiserId}
        isDcmCreative={selectedCreative && selectedCreative.dcmCreative}
        isVideoCreative={selectedCreative && selectedCreative.file && selectedCreative.file.type && selectedCreative.file.type.startsWith("video/")}
        setAlert={setAlert}
        fileDimensions={handleFileDimensions()}
        selectedCreative={selectedCreative}
        isRetina={selectedCreative ? selectedCreative.isRetina || false : false}
        onRetinaToggle={handleRetinaToggle}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        // Errors persist until dismissed; successes keep auto-hiding.
        autoHideDuration={snackbarSeverity === "error" ? null : 3000}
        open={snackbarOpen}
        onClose={(event, reason) => {
          // A stray click elsewhere shouldn't wipe an error before it's been
          // read — only the close button dismisses those.
          if (snackbarSeverity === "error" && reason === "clickaway") return;
          setSnackbarOpen(false);
        }}
        disableWindowBlurListener
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default CreativeNameManager;
